import { TilaResponse } from "@/api/models/tila-response"
import { HuonekorttiResponse } from "@/api/models/huonekortti-response"
import { computed, ref, Ref } from "vue"
import huonekorttiTableStore from "./huonekorttiTableStore"
import { useTilatunnusDuplicates } from "@/methods/tilatunnusDuplicatesComposables"
import { useLiitteet, useDownloadLiite } from "@/methods/liiteComposables"
import kommenttiTyyppi from "@/types/Kommentti"

export function useHuonekorttiTable() {
    const { getters, methods } = huonekorttiTableStore

    const huonekorttiIdsSelected = computed({
        get(): number[] {
            return getters.getSelected()
        },
        set(val: number[]): void {
            methods.setSelected(val)
        },
    })

    const formatTilat = (tilat: TilaResponse[]) => {
        if (tilat != undefined) {
            return tilat
                .map((t: TilaResponse) => {
                    return t.tilatunnus
                })
                .join(", ")
        }
    }

    return {
        huonekorttiIdsSelected,
        formatTilat,
    }
}

export function useHuonekorttiTableWithDuplicates(hankeId: Ref<number | undefined>) {
    const { huonekorttiIdsSelected, formatTilat } = useHuonekorttiTable()
    const { duplicates } = useTilatunnusDuplicates(hankeId)
    
    const getDuplicateTilatunnusList = computed(() => {
        return duplicates.value?.duplicates?.map(d => d.tilatunnus) || []
    })
    
    const isDuplicateTilatunnus = (tilatunnus: string) => {
        return getDuplicateTilatunnusList.value.includes(tilatunnus)
    }

    const isDuplicateHuonekortti = (huonekortti: HuonekorttiResponse) => {
        return huonekortti.tilat?.some(tila =>
            isDuplicateTilatunnus(tila.tilatunnus || '')
        ) || false
    }

    const getDuplicateCount = (huonekortti: HuonekorttiResponse) => {
        return huonekortti.tilat?.filter(tila =>
            isDuplicateTilatunnus(tila.tilatunnus || '')
        ).length || 0
    }
    
    return {
        huonekorttiIdsSelected,
        formatTilat,
        isDuplicateTilatunnus,
        isDuplicateHuonekortti,
        getDuplicateCount
    }
}

export function useHuonekorttiExpansion() {
    const expandedRows = ref<number[]>([])
    const selectedHuonekorttiId = ref<number | undefined>()
    const liiteTyyppi = ref<kommenttiTyyppi>(1) // Huonekortti type
    const { liitteet, loading: liitteetLoading } = useLiitteet(selectedHuonekorttiId, liiteTyyppi)

    const handleRowExpand = (row: HuonekorttiResponse, expanded: boolean) => {
        if (expanded && expandedRows.value[0] !== row.id) {
            expandedRows.value = [row.id]
            selectedHuonekorttiId.value = row.id // Fetch liitteet for this huonekortti
        } else {
            expandedRows.value = []
            selectedHuonekorttiId.value = undefined // Clear liitteet
        }
    }

    const downloadLiiteClick = async (id: number) => {
        const response = await useDownloadLiite(id)
        if (response?.url) {
            window.open(response.url, '_blank')
        }
    }

    return {
        expandedRows,
        selectedHuonekorttiId,
        liitteet,
        liitteetLoading,
        handleRowExpand,
        downloadLiiteClick
    }
}
